import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, errMsg } from "@/lib/api";
import { OrderSummary } from "@/components/site/OrderSummary";

const EMPTY = { email: "", firstName: "", lastName: "", addressLine1: "", addressLine2: "", city: "", state: "", postCode: "", country: "AU", phone: "" };
const STATE_REQUIRED = ["AU", "US", "CA"];

const Field = ({ label, name, form, set, required = true, type = "text", className = "" }) => (
  <label className={className}>
    <span className="field-label">{label}{required ? "" : " (optional)"}</span>
    <input type={type} required={required} value={form[name]} onChange={(e) => set(name, e.target.value)} className="field" data-testid={`checkout-${name}-input`} />
  </label>
);

export default function Checkout() {
  const { items } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [countries, setCountries] = useState([]);
  const [coupon, setCoupon] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payloadItems = useMemo(() => items.map((i) => ({ productId: i.productId, variantIndex: i.variantIndex, quantity: i.quantity })), [items]);

  useEffect(() => {
    api.get("/shipping/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!payloadItems.length) return;
    api
      .post("/checkout/quote", { items: payloadItems, country: form.country, couponCode: coupon })
      .then((r) => {
        setQuote(r.data);
        setQuoteErr("");
        if (coupon && r.data.couponError) {
          toast.error(r.data.couponError);
          setCoupon(null);
        }
      })
      .catch((e) => setQuoteErr(errMsg(e)));
  }, [payloadItems, form.country, coupon]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const opt = (v) => (v.trim() ? v.trim() : null);
    const address = { ...form, addressLine2: opt(form.addressLine2), state: opt(form.state), phone: opt(form.phone) };
    delete address.email;
    try {
      const { data } = await api.post("/checkout", { items: payloadItems, country: form.country, couponCode: coupon, email: form.email, address, giftMessage: gift.trim() || null });
      window.location.href = data.url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <div className="container-x py-32" data-testid="checkout-empty">
        <h1 className="font-display text-5xl font-light">Your bag is empty.</h1>
        <Link to="/gallery" className="link-u mt-10">Explore the collection</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-16 sm:py-20" data-testid="checkout-page">
      <p className="eyebrow">Checkout</p>
      <h1 className="mt-6 font-display text-5xl font-light">Delivery details</h1>
      <form onSubmit={submit} className="mt-14 grid gap-16 lg:grid-cols-12">
        <div className="grid grid-cols-2 gap-x-8 gap-y-8 lg:col-span-7">
          <Field label="Email" name="email" type="email" form={form} set={set} className="col-span-2" />
          <Field label="First name" name="firstName" form={form} set={set} />
          <Field label="Last name" name="lastName" form={form} set={set} />
          <label className="col-span-2">
            <span className="field-label">Country</span>
            <select value={form.country} onChange={(e) => set("country", e.target.value)} className="field cursor-pointer" data-testid="cart-country-select">
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </label>
          <Field label="Address" name="addressLine1" form={form} set={set} className="col-span-2" />
          <Field label="Apartment, suite" name="addressLine2" form={form} set={set} required={false} className="col-span-2" />
          <Field label="City / suburb" name="city" form={form} set={set} />
          <Field label="State / region" name="state" form={form} set={set} required={STATE_REQUIRED.includes(form.country)} />
          <Field label="Postcode" name="postCode" form={form} set={set} />
          <Field label="Phone" name="phone" type="tel" form={form} set={set} required={false} />
          <label className="col-span-2">
            <span className="field-label">Gift message (optional)</span>
            <textarea rows={3} maxLength={250} value={gift} onChange={(e) => setGift(e.target.value)} placeholder="Sending this as a gift? Add a short note and we'll include it in the parcel." className="field resize-none" data-testid="checkout-gift-message-input" />
            <span className="mt-1 block text-right text-[11px] text-[#8A8780]" data-testid="checkout-gift-message-count">{gift.length}/250</span>
          </label>
          <p className="col-span-2 text-xs leading-relaxed text-[#5A626A]">
            Payment is completed securely on Square's hosted checkout. Your card details never touch this site.
          </p>
        </div>
        <div className="lg:col-span-5">
          <OrderSummary quote={quote} error={quoteErr} coupon={coupon} setCoupon={setCoupon} busy={busy} />
        </div>
      </form>
    </div>
  );
}
